import { AgGridReact } from 'ag-grid-react';
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Badge, Dropdown, DropdownItem, DropdownMenu, DropdownToggle } from "reactstrap";
import SidebarLayout from '../../../../layouts/SidebarLayout';
import PageSubHeader from '../../../../components/Layouts/PageSubHeader';
import sidebar from '../../utils/sidebar';
import { toast } from 'react-toastify';
import DefaultButton from '../../../../components/Buttons/Default';
import { del, get, getBlob } from '../../../../utils/helpers/api';
import DefaultPagination from '../../../../components/Pagination';
import IconButton from '../../../../components/Buttons/IconButton';
import { BsTrash3, BsDownload } from 'react-icons/bs';
import { IoCreateOutline, IoCloudUploadOutline, IoTrashOutline } from 'react-icons/io5';
import { TfiFilter } from 'react-icons/tfi';
import { PiDotsThreeOutlineVerticalFill } from 'react-icons/pi';
import ConfirmationModal from '../../../../components/Modals/Confirmation';
import './styles.css';
import FiltersDrawer from './Drawers/Filters';
import ImportModal from '../../../../components/Modals/Import';
import AgGridTable from '../../../../components/Tables/AgGrid';

ModuleRegistry.registerModules([AllCommunityModule]);

function ActionsCellRenderer({ data, context }) {
  return (
    <div className="d-flex align-items-center gap-2 h-100">
      <IconButton
        onClick={(e) => {
          e.stopPropagation();
          context.navigate(`/crm/customers/edit/${data.id}`, { state: { customerData: data } });
        }}
        size="sm"
        outline
        color="secondary"
      >
        <IoCreateOutline />
      </IconButton>

      <IconButton
        onClick={(e) => {
          e.stopPropagation();
          context.setCustomerToDelete(data.id);
        }}
        size="sm"
        outline
        color="danger"
      >
        <BsTrash3 />
      </IconButton>
    </div>
  );
}

export default function CustomersPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [rowData, setRowData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState(null);
  const [moreOpen, setMoreOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);

  const FILTER_KEYS = ['name', 'company_name', 'email', 'phone_number', 'type', 'added_after', 'added_before'];

  const [filters, setFilters] = useState(() => {
    const initial = {};
    FILTER_KEYS.forEach(key => {
      const val = searchParams.get(key);
      if (val) initial[key] = val;
    });
    return initial;
  });

  const filtersCount = Object.values(filters).filter(Boolean).length;

  const getCustomers = useCallback(async () => {
    setLoading(true);
    let query = '/crm/customers?page=' + currentPage + '&page_size=' + pageSize;
    const activeFilters = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    if (Object.keys(activeFilters).length) {
      query += '&' + new URLSearchParams(activeFilters).toString();
    }
    const data = await get(query);
    if (data?.customers?.data) {
      setRowData(data.customers.data);
      setTotalPages(data.customers.last_page);
      setTotalRecords(data.customers.total);
    }
    setLoading(false);
  }, [currentPage, pageSize, filters]);

  useEffect(() => {
    getCustomers();
  }, [getCustomers]);

  const handleApplyFilters = (filtersData) => {
    const activeFilters = Object.fromEntries(Object.entries(filtersData).filter(([, v]) => v));
    setFilters(activeFilters);
    setSearchParams(activeFilters);
    setCurrentPage(1);
    setFiltersOpen(false);
  };

  const exportCustomers = async () => {
    const data = await getBlob('/crm/customers/export')
      .then(blob => {
        if (!blob) throw new Error('export failed');
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `customers-${new Date().toISOString()}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      })
      .catch(err => {
        toast.error(t('crm.exportError'));
      });
  }

  const deleteCustomer = async () => {
    const data = await del('/crm/customers/delete/' + customerToDelete);

    if (data.success) {
      toast.success(data.message || t('crm.customerDeleted'));
      getCustomers();
    } else {
      toast.error(data.message || t('common.somethingWentWrong'));
    }
    setCustomerToDelete(null);
  };

  const columnDefs = useMemo(() => [
    {
      headerName: t('common.name'),
      minWidth: 180,
      flex: 1,
      sortable: true,
      valueGetter: (p) => [p.data.firstname, p.data.middlename, p.data.lastname].filter(Boolean).join(' '),
    },
    { field: 'company_name', headerName: t('common.companyName'), minWidth: 200, flex: 1, sortable: true },
    { field: 'primary_email', headerName: t('common.email'), minWidth: 180, sortable: true },
    { field: 'primary_phone', headerName: t('common.phone'), minWidth: 150, sortable: true },
    { field: 'country', headerName: t('common.country'), minWidth: 140, sortable: true },
    {
      headerName: t('common.actions'),
      minWidth: 100,
      sortable: false,
      resizable: false,
      cellRenderer: ActionsCellRenderer,
    },
  ], [t]);

  const defaultColDef = useMemo(() => ({
    resizable: true,
  }), []);

  const gridContext = useMemo(() => ({ navigate, setCustomerToDelete }), [navigate]);

  const onRowClicked = (event) => {
    if (event.event?.target?.closest('button')) return;
    navigate(`/crm/customers/view/${event.data.id}`, { state: { customerData: event.data } });
  };

  return (
    <SidebarLayout sidebar={sidebar}>
      <div className="page-with-subheader">
        <PageSubHeader title={t('crm.customers')} onReload={getCustomers} dataTotal={totalRecords}>
          <button className="subheader-btn" type="button" onClick={() => setFiltersOpen(true)}>
            <TfiFilter size={13} />
            {t('common.filter')}
            {filtersCount > 0 && (
              <Badge color="primary" className="filter-badge">{filtersCount}</Badge>
            )}
          </button>
          <div className="subheader-divider" />
          <DefaultButton
            color="primary"
            size="sm"
            onClick={() => navigate('/crm/customers/create')}
          >
            {t('crm.newCustomer')}
          </DefaultButton>
          <Dropdown isOpen={moreOpen} toggle={() => setMoreOpen(o => !o)} size="sm">
            <DropdownToggle outline color="secondary">
              <PiDotsThreeOutlineVerticalFill />
            </DropdownToggle>
            <DropdownMenu end>
              <DropdownItem onClick={() => setImportModalOpen(true)}>
                <IoCloudUploadOutline size={15} /> {t('common.import')}
              </DropdownItem>
              <DropdownItem onClick={exportCustomers}>
                <BsDownload size={15} /> {t('common.export')}
              </DropdownItem>
            </DropdownMenu>
          </Dropdown>
        </PageSubHeader>

        <div className="subheader-page-content">
          <AgGridTable 
            rowData={rowData}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            loading={loading}
            context={gridContext}
            onRowClicked={onRowClicked}
          />

          <DefaultPagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </div>
      </div>

      <FiltersDrawer
        isOpen={filtersOpen}
        toggle={() => setFiltersOpen(false)}
        filters={filters}
        applyFilters={handleApplyFilters}
      />

      <ImportModal
        isOpen={importModalOpen}
        toggle={() => setImportModalOpen(false)}
        onImportSuccess={getCustomers}
        module="crm"
        section="customers"
      />

      <ConfirmationModal
        isOpen={customerToDelete !== null}
        title={t('crm.deleteCustomer')}
        message={t('crm.deleteCustomerConfirm')}
        onConfirm={deleteCustomer}
        toggle={() => setCustomerToDelete(null)}
      />
    </SidebarLayout>
  );
}
